
import { SimulationConfig, Vector2D, Ball } from '@/types';

export class CameraService {
  private currentPos: Vector2D = { x: 0, y: 0 };
  private currentVel: Vector2D = { x: 0, y: 0 };
  private targetPos: Vector2D = { x: 0, y: 0 };
  private currentZoom: number = 1.0;
  private initialized: boolean = false;

  public reset() {
    this.currentPos = { x: 0, y: 0 };
    this.currentVel = { x: 0, y: 0 };
    this.targetPos = { x: 0, y: 0 };
    this.currentZoom = 1.0;
    this.initialized = true;
  }

  public update(
    canvasWidth: number,
    canvasHeight: number,
    balls: Ball[],
    config: SimulationConfig,
    shakeAmount: number
  ): { x: number, y: number, scale: number } {
    if (!config.cameraFollow || balls.length === 0) {
      if (!config.cameraFollow) {
          this.targetPos.x = 0;
          this.targetPos.y = 0;
      }
    } else {
      // Track the first ball
      const targetBall = balls[0];
      
      // 1. Calculate ideal position
      let idealX = targetBall.pos.x;
      let idealY = targetBall.pos.y;

      // 2. Advanced Lookahead
      if (config.cameraLookahead > 0) {
        const lookAheadFactor = config.cameraLookahead * 5;
        if (config.cameraPredictiveLookahead) {
            // Predictive: use current velocity to guess position in N frames
            idealX += targetBall.vel.x * lookAheadFactor;
            idealY += targetBall.vel.y * lookAheadFactor;
        } else {
            // Simple velocity offset
            idealX += targetBall.vel.x * lookAheadFactor * 0.5;
            idealY += targetBall.vel.y * lookAheadFactor * 0.5;
        }
      }

      // 3. Apply Deadzone
      const dx = idealX - this.currentPos.x;
      const dy = idealY - this.currentPos.y;
      const dist = Math.sqrt(dx*dx + dy*dy);
      
      if (dist > config.cameraDeadzone) {
         const angle = Math.atan2(dy, dx);
         const moveDist = dist - config.cameraDeadzone;
         this.targetPos.x = this.currentPos.x + Math.cos(angle) * moveDist;
         this.targetPos.y = this.currentPos.y + Math.sin(angle) * moveDist;
      }
    }

    if (!this.initialized) {
        this.currentPos = { ...this.targetPos };
        this.currentZoom = config.cameraZoom || 1.0;
        this.initialized = true;
    }

    // 4. Advanced 2nd Order Dynamics (Spring-Damper)
    // f = stiffness * (target - current) - damping * velocity
    const stiffness = config.cameraElasticity || 0.1;
    const damping = config.cameraDamping || 0.15;
    const lerpAlpha = config.cameraSmoothness || 0.08;

    // We mix Lerp and Spring for maximum "liquid" feel
    const springX = (this.targetPos.x - this.currentPos.x) * stiffness;
    const springY = (this.targetPos.y - this.currentPos.y) * stiffness;
    
    this.currentVel.x += springX;
    this.currentVel.y += springY;
    this.currentVel.x *= (1 - damping);
    this.currentVel.y *= (1 - damping);

    this.currentPos.x += this.currentVel.x;
    this.currentPos.y += this.currentVel.y;

    // Apply Lerp on top for extra stability/smoothness at low speeds
    this.currentPos.x = this.currentPos.x + (this.targetPos.x - this.currentPos.x) * lerpAlpha;
    this.currentPos.y = this.currentPos.y + (this.targetPos.y - this.currentPos.y) * lerpAlpha;

    // 5. Smooth Zoom
    const zoomAlpha = config.cameraZoomSmoothness || 0.05;
    const targetZoom = config.cameraZoom || 1.0;
    this.currentZoom += (targetZoom - this.currentZoom) * zoomAlpha;

    // Calculate final draw offset
    let offsetX = -this.currentPos.x;
    let offsetY = -this.currentPos.y;

    // Apply Shake
    if (shakeAmount > 0) {
        offsetX += (Math.random() - 0.5) * shakeAmount;
        offsetY += (Math.random() - 0.5) * shakeAmount;
    }

    return {
        x: offsetX,
        y: offsetY,
        scale: this.currentZoom
    };
  }
}

export const CAMERA = new CameraService();
